import { Injectable } from "@angular/core";
import {
    BoxGeometry,
    BufferAttribute,
    BufferGeometry,
    CircleGeometry,
    ColorRepresentation,
    CylinderGeometry,
    DoubleSide,
    EdgesGeometry,
    Euler,
    Group,
    LineBasicMaterial,
    LineSegments,
    Material,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    MeshStandardMaterial,
    Object3D,
    Path,
    Scene,
    SphereGeometry,
    TextureLoader,
    Vector3,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { Subject, Observable, firstValueFrom } from "rxjs";
import { AlteredShapeConfiguration, Object3DUserData } from "../../../../../utils/shape";
import { S3UploadService } from "../s3-upoad/s3-upload.service";
import { FilenameService } from "../filename/filename.service";
import { HelperService } from "../../shared/helpers/helper.service";
import { CellReference } from "@models/mesh.model";
import { StructureResponseMessage } from "@models/structure.model";
import { AuxiliaryObjectType, CurveType, MeshType, ShapeType } from "~/src/utils/shape-type";
import { ColorCodes } from "~/src/utils/color-constants";
import { SetShapeUserDataService } from "../../shared/helpers/set-shape-userdata.service";
import { StructureService } from "../structure/structure.service";
import * as MergeBuffer from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { ColorHEX } from "~/../utils/action";
import { HalfScreenCanvasConfig } from "../../../../../utils/canvas-configuration";
import { ShapeResourceManagerService } from "../../shared/helpers/shape-resource-manager.service";
import { Face } from "~/src/utils/count-calculator/count-calculator.model";
import { FaceMetaDataMap, getOriginalFaceMetaDataMap } from "~/src/utils/shape-facetype";
import { ShapeGeneratorService } from "../shape-generator/shape-generator.service";

@Injectable({
    providedIn: "root",
})
export class SceneManagerService {
    public scene: Scene = new Scene();
    private loader: GLTFLoader;
    private exporter: GLTFExporter;
    public isSaveAsGLB: boolean = false;

    private objectsSubject: Subject<Object3D[]> = new Subject<Object3D[]>();
    public objects$: Observable<Object3D[]> = this.objectsSubject.asObservable();

    private meshesSubject: Subject<Mesh[]> = new Subject<Mesh[]>();
    public meshes$: Observable<Mesh[]> = this.meshesSubject.asObservable();

    private geometriesData: any[] = [];
    private materialCache: Map<string, Material>;

    private objects: Object3D[] = [];
    private meshes: Mesh[] = [];

    constructor(
        private helperService: HelperService,
        private filenameService: FilenameService,
        private s3UploadService: S3UploadService,
        private setShapeUserDataService: SetShapeUserDataService,
        private structureService: StructureService,
        private shapeResourceManagerService: ShapeResourceManagerService,
        private shapeGeneratorService: ShapeGeneratorService,
    ) {
        this.loader = new GLTFLoader();
        this.exporter = new GLTFExporter();
        this.materialCache = new Map();
    }
    /**
     * Set the Three.js scene where objects will be added
     * @param scene - The Three.js scene
     */
    public setScene(scene: Scene): void {
        this.scene = scene;
        this.objects = this.scene.children;
        this.objectsSubject.next(this.objects);

        this.scene.children
            .filter((child): child is Mesh => child instanceof Mesh && child.name !== AuxiliaryObjectType.Plane)
            .forEach((mesh) => {
                this.meshes.push(mesh);
                this.meshesSubject.next(this.meshes);
            });
    }
    /**
     * Get the current Three.js scene
     * @returns scene - The Three.js scene
     */
    public getScene(): Scene {
        return this.scene;
    }

    /**
     * Get all meshes in the scene, excluding Plane and other objects.
     * @returns Array of meshes in the scene excluding plane and other objects.
     */
    public getMeshes(): Mesh[] {
        if (!this.scene) {
            return [];
        }

        // Filter the scene's children to return only meshes that are not of type Plane or Light
        return [...this.meshes];
    }

    /**
     * Add objects to the scene and update the scene state
     * @param objects - Objects to add to the scene
     */
    public addObjectsToScene(objects: Object3D[]): void {
        this.scene.add(...objects);
        this.objects = objects;
        this.objectsSubject.next(this.scene.children);

        this.objects
            .filter((child): child is Mesh => child instanceof Mesh && child.name !== AuxiliaryObjectType.Plane)
            .forEach((mesh) => {
                this.meshes.push(mesh);
                this.meshesSubject.next(this.meshes);
                // Re-establish adjacent data
                this.setShapeUserDataService.adjacentData(this.meshes, mesh as Mesh);
            });
    }

    public removeAllObjectsFromScene(): void {
        // Dispose of geometries
        this.scene.traverse((object) => {
            if (object instanceof Mesh) {
                object.geometry.dispose();
                if (Array.isArray(object.material)) {
                    object.material.forEach((material) => material.dispose());
                } else {
                    object.material.dispose();
                }
            }
        });

        this.scene.children = [];
        this.objects = this.scene.children;
        this.objectsSubject.next(this.objects);
        this.meshes = [];
        this.meshesSubject.next(this.meshes);
    }

    /**
     * Removes all meshes from the scene except the plane
     */
    public removeAllMeshesFromScene(): void {
        this.scene.children = this.scene.children.filter(
            (child) => !(child instanceof Mesh) || child.name == AuxiliaryObjectType.Plane,
        );
        this.objects = this.scene.children;
        this.objectsSubject.next(this.objects);

        this.meshes = [];
        this.meshesSubject.next(this.meshes);
    }

    /**
     * Removes selected meshes from the scene
     */
    public removeMeshesFromScene(deletingMeshes: Mesh[]): Mesh[] {
        let existingMeshes: Mesh[] = this.getMeshes();

        if (!existingMeshes.length) {
            return [];
        }

        // Convert deletingMeshes to a Set for faster lookups
        const deletingMeshesSet = new Set(deletingMeshes.map((mesh) => mesh.uuid));

        // First, update face types before we clear adjacentData
        existingMeshes.forEach((mesh) => {
            const adjacentData = mesh.userData[Object3DUserData.adjacentData];
            if (adjacentData) {
                Object.keys(Face).forEach((faceKey) => {
                    const face = faceKey as Face;
                    const adjacentShapeUUID = adjacentData[face];
                    if (adjacentShapeUUID && deletingMeshesSet.has(adjacentShapeUUID)) {
                        let faceMetaData = (mesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap)[face];
                        if (faceMetaData) {
                            faceMetaData.type = faceMetaData.originalType;
                        }
                    }
                });
            }
        });

        // Loop through and remove meshes
        existingMeshes = existingMeshes.filter((mesh) => {
            // If the shape is in deletingMeshes, remove it
            if (deletingMeshesSet.has(mesh.uuid)) {
                // Remove the mesh from the scene and clean up user data
                this.setShapeUserDataService.clearAdjacentDataOnDragStart(mesh, existingMeshes);
                this.scene.remove(mesh);
                this.objectsSubject.next(this.scene.children);

                if (mesh instanceof Mesh && mesh.name !== AuxiliaryObjectType.Plane) {
                    this.meshes.splice(this.meshes.indexOf(mesh), 1);
                    this.meshesSubject.next(this.meshes);
                }

                // Clean up resources before removal
                this.cleanupMeshResources(mesh);

                return false; // Don't keep the mesh
            }

            return true; // Keep the mesh
        });

        return existingMeshes;
    }

    /**
     * Cleans up mesh resources to prevent memory leaks
     */
    private cleanupMeshResources(mesh: Mesh): void {
        // Cleanup child elements
        mesh.children.forEach((child) => {
            if (child instanceof LineSegments || child instanceof Mesh) {
                if (child.geometry) {
                    child.geometry.dispose();
                }
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach((material) => material.dispose());
                    } else {
                        (child.material as Material).dispose();
                    }
                }
            }
        });

        // Cleanup main mesh
        if (mesh.geometry) {
            mesh.geometry.dispose();
        }
        if (mesh.material) {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((material) => material.dispose());
            } else {
                (mesh.material as Material).dispose();
            }
        }
    }

    /**
     * Filter meshes in the scene for export
     * @returns An array of meshes to export
     */
    private getMeshesToExport(): Mesh[] {
        return this.scene.children.filter(
            (child) => child instanceof Mesh && child.name !== AuxiliaryObjectType.Plane,
        ) as Mesh[];
    }

    /**
     * Export the scene and upload it to S3
     * @param categoryName - S3 category for the upload
     * @param fileType - The file type (e.g., 'images', 'videos', 'glb-files')
     * @param filename - Optional filename
     * @returns The uploaded URL
     */
    public exportToS3(categoryName: string, fileType: string, filename?: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const finalFilename = this.filenameService.generateTimestampFileName(filename || "scene", "glb");
            const meshesToExport = this.getMeshesToExport();
            const exportOptions = { trs: false, onlyVisible: true, binary: true };

            this.exporter.parse(
                meshesToExport,
                async (gltf) => {
                    if (gltf instanceof ArrayBuffer) {
                        try {
                            const file = new File([gltf], finalFilename, { type: "model/gltf-binary" });
                            const uploadedUrls = await this.s3UploadService.multipartOperation(
                                [file],
                                categoryName,
                                fileType,
                            );
                            resolve(uploadedUrls[0]);
                        } catch (error) {
                            reject(error);
                        }
                    } else {
                        reject(new Error("Unexpected export result"));
                    }
                },
                reject,
                exportOptions,
            );
        });
    }

    /**
     * Extracts the file extension from a given URL.
     *
     * This function splits the URL by the dot (`.`) character and returns the last segment as the file extension,
     * converting it to lowercase to ensure case-insensitivity. If no valid extension is found, an empty string is returned.
     *
     * @param url - The URL from which to extract the file extension.
     * @returns The file extension in lowercase, or an empty string if no extension is found.
     */
    public getFileExtension(url: string): string {
        const extension = url.split(".").pop()?.toLowerCase();
        return extension ?? "";
    }

    /**
     * Load a GLTF/GLB file and add its children to the scene
     * @param glbUrl - URL or file path to the GLTF/GLB file
     */
    public loadFromGlbUrl(glbUrl: string, isUpdated: boolean = false): Promise<void> {
        return new Promise((resolve, reject) => {
            this.loader.load(
                glbUrl,
                (gltf) => {
                    this.cleanAndProcessScene(gltf.scene);
                    this.addObjectsToScene([...gltf.scene.children]);
                    this.recalculateCellReferences(isUpdated);
                    resolve();
                },
                this.onProgress.bind(this),
                (error) => {
                    reject(error);
                },
            );
        });
    }

    /**
     * Logs loading progress
     * @param xhr - XMLHttpRequest object
     */
    private onProgress(xhr: ProgressEvent): void {
        // Progress handler
        if (xhr.lengthComputable) {
            const percentComplete = (xhr.loaded / xhr.total) * 100;
        }
    }

    /**
     * Clean up mesh names and process userdata recursively
     * @param parent - Parent object in the scene (e.g., Mesh or Group)
     */
    private cleanAndProcessScene(group: Group): void {
        group.children.map((mesh) => {
            if (mesh instanceof Mesh) {
                this.cleanMeshNames(mesh);
                this.processUserData(mesh);
                this.processOutLine(mesh);
            }
        });
    }

    /**
     * Recursively clean numeric suffixes from mesh and its children names
     * @param parent - The parent object (mesh, group, etc.)
     */
    private cleanMeshNames(parent: Object3D): void {
        if (parent.name) {
            parent.name = this.removeNameSuffix(parent.name);
        }
        parent.children.forEach((child) => this.cleanMeshNames(child));
    }

    /**
     * Removes numeric suffixes (e.g., "_1", "_2") from mesh names
     * @param name - The name of the object
     * @returns Cleaned name
     */
    private removeNameSuffix(name: string): string {
        return name.replace(/(_\d+|mesh_\d+)$/i, "");
    }

    /**
     * Process user data and convert properties to Three.js types
     * @param parent - The parent object in the scene
     */
    private processUserData(parent: Object3D): void {
        if (parent instanceof Mesh && parent.userData) {
            this.convertUserDataToThreeTypes(parent);
        }
        parent.children.forEach((child) => this.processUserData(child));
    }

    /**
     * Convert user data properties to Three.js types (Vector3, Material, Euler)
     * @param mesh - The mesh object with user data to process
     */
    private convertUserDataToThreeTypes(mesh: Mesh): void {
        const userData = mesh.userData;

        // Convert 'initialPosition' to Vector3 if available
        if (userData[Object3DUserData.initialPosition]) {
            const { x, y, z } = userData[Object3DUserData.initialPosition];
            mesh.userData[Object3DUserData.initialPosition] = new Vector3(x, y, z);
        }
    }

    private processOutLine(parent: Object3D) {
        if (parent.name === AuxiliaryObjectType.Outline) {
            // Check if parent exists and is a Mesh
            const parentOfOutLine = parent.parent;
            if (!parentOfOutLine || !(parentOfOutLine instanceof Mesh)) {
                console.warn("Outline object has no valid parent mesh");
                return;
            }

            // Recreate the original material
            const originalMaterial = new MeshBasicMaterial({
                color: ColorCodes.black,
            });

            // Create new EdgesGeometry from the parent mesh's geometry
            const newEdgesGeometry = new EdgesGeometry(
                parentOfOutLine.geometry,
                AlteredShapeConfiguration.standardThreshHoldAngle,
            );

            // Apply the original geometry and material
            (parent as LineSegments).geometry = newEdgesGeometry;
            (parent as LineSegments).material = originalMaterial;
        }
        parent.children.forEach((child) => this.processOutLine(child));
    }

    /**
     * Recalculate cell reference for every mesh in the scene
     * @param isUpdated - A flag indicating if the mesh is updated or not
     */
    private recalculateCellReferences(isUpdated: boolean = false): void {
        if (isUpdated) {
            return;
        }
        // Iterate over all children in the scene
        this.scene.children.forEach((child) => {
            // Check if the child is a Mesh
            if (child instanceof Mesh && child.name !== AuxiliaryObjectType.Plane) {
                this.updateCellReference(child, isUpdated);
            }

            // Recursively process child objects (in case of nested groups)
            if (child.children && child.children.length > 0) {
                child.children.forEach((nestedChild) => {
                    if (nestedChild instanceof Mesh && child.name !== AuxiliaryObjectType.Plane) {
                        this.updateCellReference(nestedChild, isUpdated);
                    }
                });
            }
        });
    }

    /**
     * Update the cell reference for a single mesh
     * @param mesh - The mesh to update the cell reference for
     * @param isUpdated - A flag indicating if the mesh is updated or not
     */
    private updateCellReference(mesh: Mesh, isUpdated: boolean): void {
        const { row, column, cell } = this.helperService.calculateRowAndColumn(mesh);
        const cellReference: CellReference = {
            row: row,
            column: column,
            cell: cell,
        };
        mesh.userData[Object3DUserData.cellReference] = cellReference;
    }

    // Load from JSON
    public async loadFromJsonUrl(jsonUrl: string, isUpdated: boolean = false): Promise<void> {
        try {
            // Convert Observable to Promise for better async/await pattern
            const jsonArray = await firstValueFrom(this.structureService.fetchStructureData(jsonUrl));

            // Validate input array
            if (!Array.isArray(jsonArray)) {
                throw new Error("Invalid JSON structure: Expected an array");
            }
            // Process all meshes concurrently for better performance
            const meshCreationPromises = jsonArray.map(async (object) => {
                if (!object?.object?.type) {
                    throw new Error("Invalid mesh object: Missing type property");
                }

                if (object.object.type !== "Mesh") {
                    throw new Error(`Invalid mesh type: ${object.object.type}`);
                }

                return this.createMesh(object, true, isUpdated);
            });

            // Wait for all mesh creations to complete
            await Promise.all(meshCreationPromises);
        } catch (error) {
            throw new Error(StructureResponseMessage.LoadFail);
        }
    }

    public createMesh(object: any, ishavingoutline: boolean, isUpdated: boolean): Mesh {
        if (!object.geometries || !object.materials) {
            throw Error(StructureResponseMessage.InvalidObject);
        }

        const materials = Array.isArray(object.materials) ? object.materials : [object.materials];
        const materialsMap = new Map(materials.map((mat: { uuid: string }) => [mat.uuid, mat]));

        // Normalize meshMaterialUUIDs to an array
        const meshMaterialUUIDs: string[] = Array.isArray(object.object.material)
            ? object.object.material
            : [object.object.material]; // Even if it's a single UUID string

        const meshMaterials = meshMaterialUUIDs.map((uuid) => materialsMap.get(uuid)); // Array of materials for the mesh itself

        let mesh: Mesh | undefined;
        let materialData: any[] = []; // Initialize as an array
        meshMaterials.forEach((material: any) => {
            if (Array.isArray(material)) {
                materialData.push(...material); // Push all items from the nested array
            } else {
                materialData.push(material); // Push single material
            }
        });

        let materialArray: Material[] | Material = [];
        const requiredMaterials =
            object.object.name === MeshType.HemiSphere || object.object.name === MeshType.Cone ? 2 : 3;

        materialArray =
            materials.length > requiredMaterials
                ? this.createMaterials(materialData)
                : this.createMaterialPreviousStructure(object.object.name, materials[0].color);

        const finalMaterial = Array.isArray(materialArray) ? materialArray : materialArray;

        const geometry = this.shapeResourceManagerService.getGeometry(object.object.name as MeshType, false);
        // Create the mesh
        if (geometry) {
            mesh = new Mesh(geometry, finalMaterial);
            mesh.name = object.object.name;

            // Set initial position, rotation, and other properties
            mesh.position.set(
                object.object.userData?.initialPosition?.x || 0,
                object.object.userData?.initialPosition?.y || 0,
                object.object.userData?.initialPosition?.z || 0,
            );

            // Handle rotation if any
            if (object.object.userData?.rotation) {
                mesh.rotation.set(
                    object.object.userData?.rotation?.x,
                    object.object.userData?.rotation?.y,
                    object.object.userData?.rotation?.z,
                    object.object.userData?.rotation?.order,
                );
            }

            // Add outline if needed
            if (ishavingoutline) {
                this.shapeResourceManagerService.addOutlineToMesh(mesh, false);
            }

            // Set the userData and name for the mesh
            mesh.userData = {
                ...object.object.userData,
                initialPosition: mesh.position,
                rotation: {
                    x: mesh.rotation.x,
                    y: mesh.rotation.y,
                    z: mesh.rotation.z,
                    order: mesh.rotation.order,
                },
            };
            mesh.name = object.object.name;
            mesh.uuid = object.object.uuid;
        }

        // Recalculate cell reference
        if (mesh && !isUpdated) {
            const { row, column, cell } = this.helperService.calculateRowAndColumn(mesh as Mesh);

            const cellReference: CellReference = {
                row: row,
                column: column,
                cell: cell,
            };
            mesh.userData[Object3DUserData.cellReference] = cellReference;

            this.migrateLegacyFaceTypeFormat(mesh);
            this.migrateLegacyOriginalColors(mesh);
        }
        this.addObjectsToScene([mesh!]);
        return mesh!;
    }

    private migrateLegacyFaceTypeFormat(mesh: Mesh): void {
        const originalFaceMetaDataMap = getOriginalFaceMetaDataMap(mesh.name as MeshType) as FaceMetaDataMap;
        const oldFaceType = mesh.userData["faceType"];

        const newFaceMetaDataMap: FaceMetaDataMap = originalFaceMetaDataMap;

        if (oldFaceType) {
            for (const faceKey in oldFaceType) {
                const face = faceKey as Face;
                const originalData = originalFaceMetaDataMap[face];
                const oldData = oldFaceType[face];
                newFaceMetaDataMap[face] = {
                    originalType: originalData.originalType, // Save original as originalType
                    type: oldData.type ?? oldData, // Initially same, can change dynamically
                    materialIndex: originalData.materialIndex,
                };
            }
            delete mesh.userData["faceType"];
        }

        mesh.userData[Object3DUserData.faceMetaDataMap] = newFaceMetaDataMap;
    }

    private migrateLegacyOriginalColors(mesh: Mesh): void {
        const originalColors: string[] = [];
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((material) => {
                if (material instanceof MeshBasicMaterial) {
                    originalColors.push("#".concat(material.color.getHexString().toUpperCase()));
                }
            });
        }
        mesh.userData[Object3DUserData.originalColors] = originalColors;
    }

    private createMaterialPreviousStructure(type: MeshType, color: ColorRepresentation): Material[] | Material {
        return this.shapeGeneratorService.createMaterials(type, color, false);
    }

    public createMeshForBlock(
        jsonData: any,
        ishavingoutline: boolean,
        color: ColorRepresentation | string,
        useExistingMaterial: boolean = false,
    ): Mesh | null {
        try {
            this.cacheGeometriesAndMaterials(jsonData, color, useExistingMaterial);
            // Create geometry
            const geometry = this.createGeometryForBlock(jsonData.object.geometry, jsonData);
            const materials = Array.isArray(jsonData.materials) ? jsonData.materials : [jsonData.materials];
            const texturesData = Array.isArray(jsonData.images) ? jsonData.images : [jsonData.images];
            const textureLoader = new TextureLoader();
            if (!geometry) return null;

            // If no textures were processed, create a default material
            const defaultMaterial = this.materialCache.get(jsonData.object.material);

            let materialData: any[] = []; // Initialize as an array
            materials.forEach((material: any) => {
                if (Array.isArray(material)) {
                    materialData.push(...material); // Push all items from the nested array
                } else {
                    materialData.push(material); // Push single material
                }
            });
            let materialArray: Material[] | Material = [];

            materialArray = this.createMaterialsForBlock(texturesData, textureLoader, color, materialData);

            // Create the mesh with appropriate material
            const mesh = new Mesh(geometry, materialArray);

            // Apply matrix transformation if present
            if (jsonData.object.matrix) {
                const matrix = new Matrix4();
                matrix.fromArray(jsonData.object.matrix);
                mesh.applyMatrix4(matrix);
            }

            // Set name and other properties
            mesh.name = jsonData.object.name || "";

            // Handle any additional properties from the JSON
            if (jsonData.object.userData) {
                mesh.userData = { ...jsonData.object.userData };
            }

            // Add outline if needed
            if (ishavingoutline) {
                if (geometry instanceof BoxGeometry) {
                    const edges = new EdgesGeometry(mesh.geometry);
                    const lineMaterial = new LineBasicMaterial({ color: Number(ColorCodes.black) });
                    const lineSegments = new LineSegments(edges, lineMaterial);
                    mesh.add(lineSegments);
                } else {
                    const edgesGeometry = new EdgesGeometry(
                        geometry,
                        AlteredShapeConfiguration.standardThreshHoldAngle,
                    );
                    const edgesMaterial = new LineBasicMaterial({ color: Number(ColorCodes.black) });
                    const edges = new LineSegments(edgesGeometry, edgesMaterial);
                    mesh.add(edges);
                }
            }

            return mesh;
        } catch (error) {
            return null;
        }
    }

    private createGeometryForBlock(geometryUuid: string, jsonData: any): BufferGeometry | null | undefined {
        const geometryData = this.findGeometryByUuid(geometryUuid);
        if (!geometryData) return null;
        let geometry: BufferGeometry | null = null;

        if (jsonData.object.name === MeshType.HemiSphere) {
            const radius = HalfScreenCanvasConfig.hemisphereRadius;
            const widthSegments = HalfScreenCanvasConfig.hemisphereWidthSegments;
            const heightSegments = HalfScreenCanvasConfig.hemisphereHeightSegments;

            const hemisphereGeometry = new SphereGeometry(
                radius,
                widthSegments,
                heightSegments,
                0,
                Math.PI * 2,
                0,
                Math.PI / 2,
            );

            const baseGeometry = new CircleGeometry(radius, widthSegments);
            baseGeometry.rotateX(Math.PI / 2);

            geometry = (MergeBuffer as any).mergeGeometries([hemisphereGeometry, baseGeometry]);

            // Add groups for different materials
            geometry?.addGroup(0, hemisphereGeometry.index!.count, 0);
            geometry?.addGroup(hemisphereGeometry.index!.count, baseGeometry.index!.count, 1);
            geometry?.center();
        } else {
            switch (geometryData.type) {
                case ShapeType.Geometry.BoxGeometry:
                    geometry = new BoxGeometry(geometryData.width, geometryData.height, geometryData.depth);
                    geometry.center();
                    break;

                case ShapeType.Geometry.BufferGeometry:
                    if (geometryData.data && jsonData.object.name !== MeshType.Cube) {
                        // Create a new BufferGeometry instance
                        geometry = new BufferGeometry();

                        const attributes = geometryData.data.attributes;

                        // Reconstruct attributes like position and normal
                        for (const key in attributes) {
                            const attribute = attributes[key];
                            const bufferAttribute = new BufferAttribute(
                                new Float32Array(attribute.array),
                                attribute.itemSize,
                                attribute.normalized || false,
                            );
                            geometry.setAttribute(key, bufferAttribute);
                        }

                        // Reconstruct groups if they exist
                        if (geometryData.data.groups) {
                            geometryData.data.groups.forEach((group: any) => {
                                geometry?.addGroup(group.start, group.count, group.materialIndex);
                            });
                        }
                    } else {
                        return;
                    }
                    geometry.center();
                    break;

                case ShapeType.Geometry.CylinderGeometry:
                    geometry = new CylinderGeometry(
                        geometryData.radiusTop,
                        geometryData.radiusBottom,
                        geometryData.height,
                        geometryData.radialSegments,
                        geometryData.heightSegments,
                        false,
                        geometryData.thetaStart,
                        geometryData.thetaLength,
                    );
                    geometry.center();
                    break;

                case "EdgesGeometry":
                    const baseGeometry = this.createGeometryForBlock(geometryData.geometry.uuid, jsonData);
                    if (baseGeometry) {
                        geometry = new EdgesGeometry(baseGeometry, geometryData.thresholdAngle);
                    }
                    break;
            }
        }

        return geometry;
    }

    private findGeometryByUuid(uuid: string): any {
        return uuid && this.geometriesData?.find((g: any) => g.uuid === uuid);
    }

    private cacheGeometriesAndMaterials(
        jsonData: any,
        color: ColorRepresentation | string,
        useExistingMaterial: boolean,
    ): void {
        this.geometriesData = jsonData.geometries || [];

        // Cache materials
        if (jsonData.materials) {
            jsonData.materials.forEach((materialData: any) => {
                let material: Material;

                if (useExistingMaterial) {
                    switch (materialData.type) {
                        case ShapeType.Material.MeshBasicMaterial:
                            material = new MeshBasicMaterial({
                                color: materialData.color,
                                side: DoubleSide,
                                transparent: false,
                            });
                            break;
                        default:
                            material = new MeshStandardMaterial({
                                color: materialData.color,
                                side: DoubleSide,
                                transparent: false,
                            });
                            break;
                    }
                } else {
                    material = new MeshStandardMaterial({
                        color: materialData.color,
                        side: DoubleSide,
                        transparent: false,
                    });
                }

                this.materialCache.set(materialData.uuid, material);
            });
        }
    }

    // Helper function for creating materials

    private createMaterials(materialData: any): Material[] | Material {
        const materialArray: Material[] = [];
        let material: Material | undefined;
        materialData.forEach((materialData: any, index: number) => {
            materialArray.push(
                new MeshBasicMaterial({
                    color: materialData.color,
                    side: DoubleSide,
                    transparent: false,
                    name: materialData?.name ? materialData?.name : "NoName",
                }),
            );
        });

        return materialArray.length > 0 ? materialArray : material || new MeshBasicMaterial({ color: "#117A65" });
    }

    private createMaterialsForBlock(
        texturesData: any[],
        textureLoader: TextureLoader,
        selectedColour: ColorRepresentation | string,
        materialData: any,
    ): Material[] | Material {
        const materialArray: Material[] = [];
        const color = selectedColour || ColorHEX.greencyan;
        let material: Material | undefined;
        texturesData.forEach((textured: any) => {
            if (textured) {
                const texture = textureLoader.load(textured.url);
                materialArray.push(
                    new MeshBasicMaterial({
                        map: texture,
                        color: selectedColour as ColorRepresentation,
                        transparent: true,
                        side: DoubleSide,
                    }),
                );
            } else {
                materialData.forEach((materialData: any, index: number) => {
                    materialArray.push(
                        new MeshStandardMaterial({
                            color: color as ColorRepresentation,
                            side: DoubleSide,
                            transparent: false,
                            name: materialData?.name ? materialData?.name : "NoNname",
                        }),
                    );
                });
            }
        });

        return materialArray.length > 0 ? materialArray : material || new MeshBasicMaterial({ color: "#117A65" });
    }

    private createPathFromCurves(curveData: any) {
        const path = new Path();
        curveData.forEach((curve: any) => {
            if (curve.type === CurveType.EllipseCurve) {
                path.absellipse(
                    curve.aX,
                    curve.aY,
                    curve.xRadius,
                    curve.yRadius,
                    curve.aStartAngle,
                    curve.aEndAngle,
                    curve.aClockwise,
                    curve.aRotation || 0,
                );
            } else if (curve.type === CurveType.LineCurve) {
                path.lineTo(curve.v2[0], curve.v2[1]);
            } else if (curve.type === "Path" && curve.curves) {
                path.add(this.createPathFromCurves(curve.curves));
            }
        });
        return path;
    }

    public hasShapes(): boolean {
        return this.scene.children.some(
            (obj) => obj instanceof Mesh && obj.name !== "plane", // Ignore plane
        );
    }
}
